/**
 * Señales sonoras. iOS web no tiene los sonidos del sistema ni vibración: los pitidos se sintetizan como pequeños WAV
 * y se reproducen con <audio>, la misma vía que en la prueba de la fase 0 sonó con el modo silencio y sin cortar la
 * música. iOS solo deja reproducir audio tras un toque: `unlock()` (llamado en un toque) deja listos los elementos
 * para que luego suenen desde los temporizadores. Todo falla en silencio: el entreno sigue aunque no haya sonido.
 */

type Note = [freq: number, ms: number];

function wav(notes: Note[], volume = 0.5): string {
  const rate = 22050;
  const total = notes.reduce((n, [, ms]) => n + Math.round((rate * ms) / 1000), 0);
  const data = new Int16Array(total);
  let i = 0;
  for (const [freq, ms] of notes) {
    const len = Math.round((rate * ms) / 1000);
    for (let k = 0; k < len; k++, i++) {
      const env = Math.min(1, k / (rate * 0.005), (len - k) / (rate * 0.02)); // ataque y caída suaves (sin chasquidos)
      data[i] = freq ? Math.round(Math.sin((2 * Math.PI * freq * k) / rate) * env * volume * 32767) : 0;
    }
  }
  const buf = new ArrayBuffer(44 + data.length * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, j) => v.setUint8(o + j, c.charCodeAt(0)));
  str(0, 'RIFF'); v.setUint32(4, 36 + data.length * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, data.length * 2, true);
  new Int16Array(buf, 44).set(data);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}

const TONES = {
  tick: () => wav([[880, 90]]),
  go: () => wav([[1320, 260]], 0.6),
  nextRound: () => wav([[660, 110], [0, 40], [990, 160]]),
  roundDone: () => wav([[1047, 120], [1319, 220]]),
  alarm: () => wav([[880, 180], [0, 90], [880, 180], [0, 90], [880, 260]], 0.6),
  extra: () => wav([[1568, 70], [2093, 110]], 0.4),
} as const;
type Tone = keyof typeof TONES;

const elements = new Map<string, HTMLAudioElement>();

function element(key: string, src: () => string): HTMLAudioElement {
  let el = elements.get(key);
  if (!el) { el = new Audio(src()); el.preload = 'auto'; elements.set(key, el); }
  return el;
}

const fileEl = (name: string) => element(`file:${name}`, () => `/sounds/${name}.mp3`);

function play(el: HTMLAudioElement) {
  try { el.currentTime = 0; void el.play().catch(() => { /* bloqueado: sin sonido */ }); } catch { /* sin audio */ }
}

export const cues = {
  /** Llamar dentro de un toque: desbloquea los sonidos que se usarán después desde temporizadores. */
  unlock(celebration?: string) {
    const els = [...(Object.keys(TONES) as Tone[]).map(t => element(t, TONES[t])), ...(celebration && celebration !== 'none' ? [fileEl(celebration)] : []), fileEl('gamestart')];
    for (const el of els) {
      if (el.dataset.unlocked) continue;
      el.muted = true;
      void el.play().then(() => { el.pause(); el.currentTime = 0; el.muted = false; el.dataset.unlocked = '1'; }).catch(() => { el.muted = false; });
    }
  },
  tone: (t: Tone) => play(element(t, TONES[t])),
  file: (name: string) => { if (name && name !== 'none') play(fileEl(name)); },
  countdownTick: () => cues.tone('tick'),
  countdownGo: () => cues.tone('go'),
  nextRound: () => cues.tone('nextRound'),
  roundComplete: () => cues.tone('roundDone'),
  maxTimeReached: () => cues.tone('alarm'),
  wingChunDaily: () => cues.file('gamestart'),
  wingChunExtra: () => cues.tone('extra'),
};
